import { Component, ViewChild } from '@angular/core';
import { RaffleSummaryService } from './raffle-summary.service';
import { TableTransactionsComponent } from '../table-transactions/table-transactions.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/shared/services/auth.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-raffle-summary',
  templateUrl: './raffle-summary.component.html',
  styleUrls: ['./raffle-summary.component.scss'],
})
export class RaffleSummaryComponent {
  aReceber: number = 0;
  recebimento: number = 0;
  ticketMedioRecebimento: number = 0;
  conversao: number = 0;
  saque: number = 0;
  saquePendente: number = 0;
  saldoFinal: number = 0;

  extrato = new FormGroup({
    tipo: new FormControl<string>(''),
    status: new FormControl<string>('REALIZADO'),
    tipoBusca: new FormControl<string>(''),
    origem: new FormControl<string>(''),
    aplicacao: new FormControl<string>(''),
    responsavel: new FormControl<string>(''),
    statusWebHook: new FormControl<string>(''),

    initialDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
    endDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
  });

  transactionTypes = [
    { name: 'common.general', value: '' },
    { name: 'common.deposit', value: 'DEPOSITO' },
    { name: 'common.withdraw', value: 'SAQUE' },
  ];

  statusList: any[] = [
    { name: 'Todos', value: '' },
    { name: 'Realizado', value: 'REALIZADO' },
    { name: 'Cancelado', value: 'CANCELADO' },
    { name: 'Pendente', value: 'PENDENTE' },
    { name: 'Analise', value: 'ANALISE' },
    { name: 'Em Processamento', value: 'EM PROCESSAMENTO' },
    { name: 'Erro', value: 'ERRO' },
    { name: 'Criação de Link', value: 'CRIAÇÃO DE LINK' },
    { name: 'Aguardando Pagamento', value: 'AGUARDANDO PAGAMENTO' },
  ];

  originList = [
    { name: 'Todos', value: '' },
    { name: 'Pix', value: 'PIX' },
    { name: 'Comissão', value: 'COMISSÃO' },
    { name: 'Cripto', value: 'CRIPTO' },
    { name: 'Taxa Bancária', value: 'TAXA BANCÁRIA' },
    { name: 'Reembolso', value: 'REEMBOLSO' },
    { name: 'Tef', value: 'TEF' }
  ];

  applicationList = [
    { name: 'Todos', value: '' },
    { name: 'common.web', value: 'WEB' },
    { name: 'common.api', value: 'API' }
  ];

  webhookStatuses = [
    { name: 'Todos', value: '' },
    { name: 'Pendente', value: 'PENDENTE' },
    { name: 'Sucesso', value: 'SUCESSO' },
    { name: 'Falha', value: 'FALHA' },
  ];

  tipoBuscaList: any[] = [
    { name: 'common.description', value: 'personName' },
    { name: 'common.transactionId', value: 'id' },
    { name: 'common.externalId', value: 'idempotencyKey' },
    { name: 'common.document', value: 'cpf' },
    { name: 'common.endToEndId', value: 'endToEndId' },
  ];

  public isFilterColapsed = false;
  public isSubmitted = false;
  public isLoaded: boolean = false;
  public useThreeDecimals = false;


  private accountTimezoneOffsetMinutes: number | null = null;
  private originCurrency: string = 'BRL';

  @ViewChild(TableTransactionsComponent, { static: true }) table!: TableTransactionsComponent;

  constructor(
    private raffleSummaryService: RaffleSummaryService,
    private authService: AuthService,
    private toastr: ToastrService
  ) {

    this.extrato.controls.status.setValue('REALIZADO');
    this.extrato.controls.tipoBusca.setValue('personName');

    const originCurrency = (this.authService.getOriginCurrency?.() || '').toUpperCase();
    this.originCurrency = originCurrency || 'BRL';

  }

  private parseTimezoneOffset(tz: string | null | undefined): number | null {
    if (!tz) return null;

    const m = /^([+-]?)(\d{1,2}):(\d{2})$/.exec(tz.trim());
    if (!m) return null;

    const sign = m[1] === '-' ? 1 : -1;
    const hours = Number(m[2]);
    const minutes = Number(m[3]);


    return sign * (hours * 60 + minutes);
  }

  private nowInAccountTz(): Date {
    const now = new Date();

    if (this.accountTimezoneOffsetMinutes == null) {
      return now;
    }

    const browserOffset = now.getTimezoneOffset();
    const diffMinutes = this.accountTimezoneOffsetMinutes - browserOffset;

    return new Date(now.getTime() - diffMinutes * 60000);
  }


  private buildDefaultRange(hasFlag: boolean): { start: Date; end: Date } {
    const origin = (this.originCurrency || 'BRL').toUpperCase();

    if (origin === 'BRL') {
      const now = new Date();
      const tempEnd = new Date(now);

      const start = hasFlag
        ? (() => {
            const d = new Date(now.getTime() - 15 * 60 * 1000);
            d.setSeconds(0, 0);
            return d;
          })()
        : (() => {
            const d = new Date(now);
            d.setHours(0, 0, 0, 0);
            return d;
          })();

      const end = this.formatSeconds(tempEnd);
      return { start, end };
    }

    // Não-BRL: usa o fuso da conta
    const nowAcc = this.nowInAccountTz();
    const tempEnd = new Date(nowAcc);

    const start = hasFlag
      ? (() => {
          const d = new Date(nowAcc.getTime() - 15 * 60 * 1000);
          d.setSeconds(0, 0);
          return d;
        })()
      : (() => {
          const d = new Date(nowAcc);
          d.setHours(0, 0, 0, 0);
          return d;
        })();

    const end = this.formatSeconds(tempEnd);
    return { start, end };
  }

  async ngOnInit(): Promise<void> {
    try {
      const account = await firstValueFrom(this.authService.getAccount());
      const hasFlag = !!account?.featureFlagNewParam;

      this.useThreeDecimals = hasFlag;

      this.originCurrency =
        ((account as any)?.originCurrency ||
          this.authService.getOriginCurrency?.() ||
          'BRL').toUpperCase();
      this.accountTimezoneOffsetMinutes = this.parseTimezoneOffset(
        (account as any)?.timezone
      );

      const { start, end } = this.buildDefaultRange(hasFlag);

      this.extrato.controls.initialDate.setValue(start);
      this.extrato.controls.endDate.setValue(end);

      this.applyToTableAndFetch(start, end);
      this.loadScreen();

    } catch (e) {

      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);

      this.extrato.controls.initialDate.setValue(start);
      this.extrato.controls.endDate.setValue(now);
      this.loadScreen();
    }
  }

  private sanitizeDocument(value: string | null | undefined): string {
    return (value || '').replace(/\D/g, '');
  }

  private formatSeconds(v: any): Date {
    const d = this.asDate(v);
    const copy = new Date(d);
    copy.setSeconds(59, 999);
    return copy;
  }

  private toLocalIso(v: any): string {
    const dt = this.asDate(v);
    const pad = (n: number) => String(n).padStart(2, '0');
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mm = pad(dt.getMinutes());
    const ss = pad(dt.getSeconds());
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
  }

  private toNgb(v: any) {
    const d = this.asDate(v);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : v as Date;
  }

  private applyToTableAndFetch(start: Date, tempEnd: Date) {
    this.table.page = 1;
    const end = this.formatSeconds(tempEnd);
    this.table.dataInitial = this.toNgb(start);
    this.table.dataFinal = this.toNgb(end);
    this.table.startDateIso = this.toLocalIso(start);
    this.table.endDateIso = this.toLocalIso(end);

    this.table.selectedStatus = this.extrato.controls.status.value ?? '';
    this.table.transactionType = this.extrato.controls.tipo.value ?? '';
    this.table.selectedOrigin = (this.extrato.controls.origem.value ?? '').toUpperCase();
    this.table.selectedApplciation = this.extrato.controls.aplicacao.value ?? '';

    const rawSearchType = this.extrato.controls.tipoBusca.value ?? '';
    let personName = this.extrato.controls.responsavel.value ?? '';
    if (rawSearchType === 'cpf') {
      personName = this.sanitizeDocument(personName);
    }

    this.table.personName = personName;
    this.table.searchType = rawSearchType;
    this.table.selectedWebhookStatus = this.extrato.controls.statusWebHook.value ?? '';

    this.table.getTransactions();
  }

  loadScreen() {
    let start = this.extrato.controls.initialDate.value!;
    let end = this.formatSeconds(this.extrato.controls.endDate.value!);
    let dateInitialFormat = this.toLocalIso(start);
    let dateFinalFormat = this.toLocalIso(end);
    let origin = (this.extrato.controls.origem.value ?? '').toUpperCase();
    let application = this.extrato.controls.aplicacao.value ?? '';

    let searchType = this.extrato.controls.tipoBusca.value ?? '';
    let personName = this.extrato.controls.responsavel.value ?? '';
    if (searchType === 'cpf') {
      personName = this.sanitizeDocument(personName);
    }

    this.getEnum();
    this.raffleSummaryService
      .getDashBoardExtratoValues(
        dateInitialFormat,
        dateFinalFormat,
        origin,
        application,
        personName,
        searchType
      )
      .subscribe((value) => {
        this.aReceber = value.aReceber;
        this.recebimento = value.recebimento;
        this.ticketMedioRecebimento = value.ticketMedioRecebimento;
        this.conversao = value.conversao;
        this.saque = value.saque;
        this.saquePendente = value.saquePendente;
        this.saldoFinal = value.saldoFinal;
        this.isLoaded = true;
      });
  }

  getEnum(): void {
    this.raffleSummaryService.getEnum()
      .then(resp => {
        this.originList = [
          { name: 'Todos', value: '' },
          ...resp.values.map((v: string) => ({ name: v, value: v }))
        ];
      });
  }

  async paste(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.extrato.controls.responsavel.setValue(text.trim());
    } catch (err) {
      console.error('Erro ao colar o código:', err);
    }
  }

  buscar() {
    this.isSubmitted = true;
    this.isLoaded = false;

    const start = this.extrato.controls.initialDate.value!;
    const end = this.formatSeconds(this.extrato.controls.endDate.value!);

    if (start > end) {
      this.toastr.error('Data inicial deve ser menor ou igual a data final!', 'Erro', {
        timeOut: 3000, positionClass: 'toast-top-right',
      });
      this.isLoaded = true;
      return;
    }

    if (this.extrato.invalid) {
      this.isLoaded = true;
      return;
    }

    this.table.page = 1;
    this.table.dataInitial = this.toNgb(start);
    this.table.dataFinal = this.toNgb(end);

    this.table.selectedStatus = this.extrato.controls.status.value ?? '';
    this.table.transactionType = this.extrato.controls.tipo.value ?? '';
    this.table.selectedOrigin = (this.extrato.controls.origem.value ?? '').toUpperCase();
    this.table.selectedApplciation = this.extrato.controls.aplicacao.value ?? '';

    const rawSearchType = this.extrato.controls.tipoBusca.value ?? '';
    let personName = this.extrato.controls.responsavel.value ?? '';
    if (rawSearchType === 'cpf') {
      personName = this.sanitizeDocument(personName);
    }

    this.table.personName = personName;
    this.table.searchType = rawSearchType;
    this.table.selectedWebhookStatus = this.extrato.controls.statusWebHook.value ?? '';

    this.table.startDateIso = this.toLocalIso(start);
    this.table.endDateIso = this.toLocalIso(end);

    this.table.getTransactions();
    this.loadScreen();

    this.isSubmitted = false;
  }

  filterColapse() {
    this.isFilterColapsed = !this.isFilterColapsed;
  }

  downloadFile() {
    const start = this.extrato.controls.initialDate.value!;
    const end = this.extrato.controls.endDate.value!;
    this.raffleSummaryService
      .getCsvFile(start, end, this.extrato.controls.tipo.value!, this.extrato.controls.status.value!)
      .subscribe((value) => {
        let csvContent = decodeURIComponent(escape(atob(value)));
        const a = document.createElement('a');
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = window.URL.createObjectURL(blob);
        let company = this.authService.getCompany();
        a.href = url;
        a.download = `${company}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      });
  }
}
